import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  EllipsisMenuButton,
  Field,
  Flex,
  Heading,
  Input,
  Menu,
  MenuContent,
  MenuTrigger,
  Switch,
  Text,
} from '@optiaxiom/react';
import { IconArrowLeft, IconTrashCan } from '@optimizely/axiom-icons';

import { ConfirmationModal } from '@frontend/components/confirmationModal';
import { TiptapEditorWrapper } from '@frontend/components/TiptapEditor';
import { useNamespacedNavigate, useObjectTitle } from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles';

import { useInstructionReducer } from '../hooks/useInstructionReducer';
import { useUserInstruction } from '../hooks/useUserInstruction';
import { UserInstruction } from '../types';

import { styles } from './InstructionForm.css';
import UserInstructionProductScope from './UserInstructionProductScope';

export function InstructionForm() {
  const navigate = useNamespacedNavigate();
  const location = useLocation();
  const instructionToEdit = location.state as UserInstruction;
  const [formData, dispatch] = useInstructionReducer(instructionToEdit);

  // Set document title for the instruction
  useObjectTitle(formData.title);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isEditMode = location.pathname.includes('/edit/');

  const instructionType = useMemo(() => {
    return location.pathname.includes('system-instruction') ? 'system' : 'user';
  }, [location.pathname]);

  const { removeInstruction, createNewInstruction, updateInstruction, isRemoving } =
    useUserInstruction({
      instructionType: instructionType,
    });

  const handleNavigation = () => {
    if (instructionType === 'system') {
      navigate('/instructions/system');
    } else {
      navigate('/instructions');
    }
  };
  const handleDeleteInstruction = (instructionGuid: string) => {
    removeInstruction(instructionGuid);
    handleNavigation();
  };

  const handleSaveInstruction = () => {
    const instructionData = {
      ...formData,
      isSystemInstruction: instructionType === 'system',
    };

    if (instructionToEdit) {
      updateInstruction(instructionData);
    } else {
      createNewInstruction(instructionData);
    }
    handleNavigation();
  };

  return (
    <Flex className={clsx(AppPagePadding, styles.container)}>
      <Flex className={styles.stickyBackButton}>
        <Button
          appearance="subtle"
          icon={<IconArrowLeft size="12" />}
          onClick={() => navigate('/instructions')}
          w="fit"
        >
          Instructions
        </Button>
      </Flex>

      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        py="8"
        className={styles.stickyHeader}
      >
        <Flex>
          <Heading level="3" color="fg.default">
            {instructionToEdit ? 'Edit Instruction' : 'Create New Instruction'}
          </Heading>
        </Flex>
        <Flex flexDirection="row" justifyContent="center" alignItems="center">
          {instructionToEdit?.instance_id && (
            <Menu
              options={[
                {
                  addon: <IconTrashCan />,
                  intent: 'danger',
                  group: {
                    hidden: !instructionToEdit,
                    separator: true,
                  },
                  label: 'Delete Instruction',
                  execute: () => setDeleteDialogOpen(true),
                },
              ]}
            >
              <MenuTrigger asChild>
                <EllipsisMenuButton aria-label="Instruction actions menu" />
              </MenuTrigger>
              <MenuContent />
            </Menu>
          )}
          <Button appearance="primary" onClick={handleSaveInstruction} disabled={!formData.title}>
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </Flex>
      </Flex>

      <Flex flexDirection="column" gap="24">
        <Field label="Name" required>
          <Flex flexDirection="row" justifyContent="start" alignItems="center">
            <Input
              value={formData.title}
              placeholder="Instruction Name"
              className={styles.nameInput}
              onChange={e =>
                dispatch({ type: 'UPDATE_FIELD', field: 'title', value: e.target.value })
              }
            />
            <Switch
              checked={!formData.deactivated}
              size="md"
              onCheckedChange={checked =>
                dispatch({ type: 'UPDATE_FIELD', field: 'deactivated', value: !checked })
              }
            >
              Active
            </Switch>
          </Flex>
        </Field>
        <Field
          label="Core Instruction"
          info="Information and context for Opal to tailor its output. Include details like guidelines, response formats, and constraints"
        >
          <TiptapEditorWrapper
            value={formData.instruction}
            className={styles.instructionInput}
            placeholder="Enter the core instruction"
            onChange={value => dispatch({ type: 'UPDATE_FIELD', field: 'instruction', value })}
            usedIn="instruction-form"
          />
        </Field>
        <Field
          label="Where to use"
          info="Specifies a product and instance where this instruction applies. If none is selected, it will apply to all products."
        >
          <UserInstructionProductScope
            userInstruction={formData}
            onUpdate={userInstruction => dispatch({ type: 'SET_ALL', payload: userInstruction })}
          />
        </Field>
        <Field
          label="When to use"
          info="Specifies the exact conditions that will trigger this instruction, such as the user's intent, keywords, or phrases"
        >
          <Input
            value={formData.instruction_sementics}
            className={styles.instructionSementicsInput}
            placeholder='Specify when this instruction should apply, for example "when creating content"'
            onChange={e =>
              dispatch({
                type: 'UPDATE_FIELD',
                field: 'instruction_sementics',
                value: e.target.value,
              })
            }
          />
        </Field>
      </Flex>

      <ConfirmationModal
        isShown={deleteDialogOpen}
        title="Delete Instruction"
        action="Delete"
        appearance="danger"
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          handleDeleteInstruction(formData.instruction_guid);
          setDeleteDialogOpen(false);
        }}
        confirmLoading={isRemoving}
        cancelDisabled={isRemoving}
      >
        <Flex flexDirection="column" gap="4">
          <Text>
            You are about to permanently delete the Instruction{' '}
            <Text fontWeight="600">{formData.title}</Text>
          </Text>
          <Text>Do you want to continue?</Text>
        </Flex>
      </ConfirmationModal>
    </Flex>
  );
}
